import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  NAV_GROUPS,
  getNavPath,
  getActiveNavId,
  SIDEBAR_WIDTH,
  SIDEBAR_RAIL_WIDTH,
  TOPBAR_HEIGHT,
} from './navConfig';

// Ticket 2.5 — TASS pin docked at the bottom of the sidebar. Clicking it (or
// pressing ⌘J) opens the existing TASS panel via a loose window event that
// TherapistDashboard listens for. Renders compact in icon-rail mode.
const TassPin = ({ rail, onClick }) => {
  const button = (
    <ListItemButton
      onClick={onClick}
      aria-label="Open TASS assistant (⌘J)"
      sx={{ minHeight: 56, justifyContent: rail ? 'center' : 'flex-start', px: rail ? 1 : 2 }}
    >
      <ListItemIcon sx={{ minWidth: rail ? 0 : 40, justifyContent: 'center' }}>
        <Avatar src="/tass-avatar.jpg?v=2" alt="TASS" sx={{ width: 28, height: 28 }} />
      </ListItemIcon>
      {!rail && <ListItemText primary="TASS" secondary="Assistant · ⌘J" />}
    </ListItemButton>
  );
  return rail
    ? <Tooltip title="TASS assistant (⌘J)" placement="right">{button}</Tooltip>
    : button;
};

const dispatchTassToggle = () => window.dispatchEvent(new CustomEvent('aimhi:tass:toggle'));

// Grouped left navigation rail (Phase 2 shell). Responsive ladder:
//   ≥ lg (1200px) → full sidebar with labels (232px)
//   md (900–1199) → 64px icon rail with hover tooltips
//   < md          → temporary drawer opened from the top-bar hamburger
//
// The active route is highlighted and marked with aria-current="page".

const NavItems = ({ rail, activeId, onNavigate }) => (
  <List sx={{ pt: 1 }} component="nav" aria-label="Therapist navigation">
    {NAV_GROUPS.map((group) => (
      <Box key={group.id} sx={{ mb: 1 }}>
        {!rail && (
          <Typography
            variant="overline"
            sx={{ px: 2, color: 'text.secondary', fontWeight: 600, letterSpacing: 0.6 }}
          >
            {group.label}
          </Typography>
        )}
        {group.items.map((item) => {
          const selected = activeId === item.id;
          const button = (
            <ListItemButton
              component={RouterLink}
              to={getNavPath(item)}
              selected={selected}
              onClick={onNavigate}
              aria-current={selected ? 'page' : undefined}
              sx={{
                minHeight: 44,
                justifyContent: rail ? 'center' : 'flex-start',
                px: rail ? 1 : 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 140, 0, 0.10)',
                  borderRight: '3px solid #FF8C00',
                  '&:hover': { backgroundColor: 'rgba(255, 140, 0, 0.16)' },
                },
                // Phase 3 a11y — visible keyboard focus ring
                '&.Mui-focusVisible': {
                  outline: '2px solid rgba(25, 118, 210, 0.6)',
                  outlineOffset: '-2px',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: rail ? 0 : 40,
                  justifyContent: 'center',
                  color: selected ? '#FF8C00' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!rail && <ListItemText primary={item.label} />}
            </ListItemButton>
          );

          return (
            <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
              {rail ? (
                <Tooltip title={item.label} placement="right">
                  {button}
                </Tooltip>
              ) : (
                button
              )}
            </ListItem>
          );
        })}
      </Box>
    ))}
  </List>
);

const Sidebar = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // ≥ 900px
  const isFull = useMediaQuery(theme.breakpoints.up('lg'));     // ≥ 1200px
  const activeId = getActiveNavId(location);

  const railMode = isDesktop && !isFull;
  const width = isFull ? SIDEBAR_WIDTH : SIDEBAR_RAIL_WIDTH;

  if (isDesktop) {
    return (
      <Drawer
        variant="permanent"
        aria-label="Therapist navigation sidebar"
        sx={{
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            top: TOPBAR_HEIGHT,
            height: `calc(100% - ${TOPBAR_HEIGHT}px)`,
            borderRight: '1px solid #E5E7EB',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <NavItems rail={railMode} activeId={activeId} />
        </Box>
        {/* Docked TASS pin (ticket 2.5) */}
        <Box sx={{ borderTop: '1px solid #E5E7EB' }}>
          <TassPin rail={railMode} onClick={dispatchTassToggle} />
        </Box>
      </Drawer>
    );
  }

  // Mobile: temporary drawer triggered by the top-bar hamburger.
  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, boxSizing: 'border-box' } }}
    >
      <Box sx={{ height: TOPBAR_HEIGHT }} />
      <NavItems rail={false} activeId={activeId} onNavigate={onMobileClose} />
      <Box sx={{ borderTop: '1px solid #E5E7EB' }}>
        <TassPin rail={false} onClick={() => { dispatchTassToggle(); onMobileClose && onMobileClose(); }} />
      </Box>
    </Drawer>
  );
};

export default Sidebar;
